import {FormLabel, Grid, Typography} from '@mui/material';
import Button from 'components/Button';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {GridCentered} from 'components/GridCentered';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikDatePicker from 'components/inputs/FormikDatePicker';
import FormikDropdown from 'components/inputs/FormikDropdown';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikTimePicker from 'components/inputs/FormikTimePicker';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import TestDirectory from 'features/layout/CommonPage/panels/TransportPanel/components/TestDirectory';
import React, {memo, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {DiplomatObjectNames} from 'types/diplomat-object';
import {ReportType} from 'types/diplomat/reports/ReportType';
import {
   IScheduledReportsParameters,
   ScheduleReportsNames,
} from 'types/diplomat/reports/ScheduledReportsParameters';
import {RecurrenceType} from 'types/diplomat/reports/RecurrenceType';
import withStyles from '@mui/styles/withStyles';
import {DayOfMonth} from 'types/diplomat/reports/DayOfMonth';
import {useModal} from 'common/hooks/useModal';
import Dialog from 'components/Dialog';
import AuditReportForm from '../AuditReportForm';
import {IAuditReportParameters} from 'types/diplomat/reports/AuditReportParameters';
import {UserActivityReportForm} from '../UserActivityReportForm';
import {IUserActivityReportParameters} from 'types/diplomat/reports/UserActivityReportParameters';
import {SFTPActivityReportForm} from '../SFTPActivityReportForm';
import {ISFTPActivityReportParameters} from 'types/diplomat/reports/SFTPActivityReportParameters';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import {useFormContext} from 'react-hook-form';
import FormikSwitchValuable from 'components/inputs/FormikSwitchValueable';
import {enums} from 'types/enums';
import {useSelector} from 'react-redux';
import {settingsSelectors} from 'redux/settings';

export const GridFormLabel = withStyles({
   root: {
      textAlign: 'end',
   },
})(GridCentered);

type Props = {
   onClose: () => void;
};
const AddEditScheduledReportForm = memo(({onClose}: Props) => {
   const {t} = useTranslation();
   const getValue = useFieldValueGetter<IScheduledReportsParameters>();
   const {setValue} = useFormContext<IScheduledReportsParameters>();
   const settings = useSelector(settingsSelectors.auditTrailSettings);

   const isAudtiRequired = settings.auditRequired;
   const isSQL = isAudtiRequired && settings.dbSettings.dbType === enums.dbType.enum.SQL;

   const options = useMemo(
      () =>
         ReportType.options.filter((o) => {
            return !(!isSQL && o.id === ReportType.enum.USER_ACTIVITY);
         }),
      [isSQL]
   );

   const type = getValue('type');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');
   const auditParams = getValue('auditParams');
   const userParams = getValue('userParams');
   const isPDF = getValue('format') === enums.reportFormats.enum.PDF;
   const recurrenceType = getValue(
      `${ScheduleReportsNames.schedule}.${ScheduleReportsNames.recurrenceType}`
   );
   const dirTemplate = getValue(
      `${ScheduleReportsNames.location}.${ScheduleReportsNames.dirTemplate}`
   );

   const audit =
      type && (type === ReportType.enum.AUDIT_DETAIL || type === ReportType.enum.AUDIT_SUMMARY);
   const user = type && type === ReportType.enum.USER_ACTIVITY;
   const sftpActivity = type && type === ReportType.enum.SFTP_ACTIVITY;

   const {open, handleOpen, handleClose} = useModal();

   const setAuditReport = React.useCallback(
      (values: IAuditReportParameters) => {
         setValue(ScheduleReportsNames.auditParams, values);
         handleClose();
      },
      [setValue, handleClose]
   );

   const setUserReport = React.useCallback(
      (values: IUserActivityReportParameters | ISFTPActivityReportParameters) => {
         setValue(ScheduleReportsNames.userParams, values as any);
         handleClose();
      },
      [setValue, handleClose]
   );

   return (
      <>
         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('Report Type: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item>
               <FormikDropdown name={ScheduleReportsNames.type} options={options} />
            </GridCentered>
            <GridCentered item>
               {audit && <Button onClick={handleOpen}>{t('Audit Parameters')}</Button>}
               {user && <Button onClick={handleOpen}>{t('User Parameters')}</Button>}
               {sftpActivity && <Button onClick={handleOpen}>{t('SFTP Parameters')}</Button>}
            </GridCentered>
         </Grid>

         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('Format: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item>
               <FormikSwitchValuable
                  name={ScheduleReportsNames.format}
                  options={[enums.reportFormats.options[0], enums.reportFormats.options[1]]}
               />
            </GridCentered>
         </Grid>

         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('Report Name: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item>
               <FormikTextField name={DiplomatObjectNames.name} />
            </GridCentered>
         </Grid>

         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('Report Directory: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item>
               <FormikBrowseFileInput
                  name={`${ScheduleReportsNames.location}.${ScheduleReportsNames.dirTemplate}`}
                  directoriesOnly
               />
            </GridCentered>
            <GridCentered item title={t('Test report directory location')}>
               <TestDirectory directoryPath={dirTemplate} />
            </GridCentered>
         </Grid>

         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('File Name: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item>
               <FormikTextField
                  name={`${ScheduleReportsNames.location}.${ScheduleReportsNames.fileTemplate}`}
                  InputProps={{
                     endAdornment: (
                        <Typography variant={'body2'}>{isPDF ? t('.pdf') : t('.csv')}</Typography>
                     ),
                  }}
               />
            </GridCentered>
         </Grid>

         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('First Scheduled Report: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item xs={5}>
               <FormikDatePicker
                  name={`${ScheduleReportsNames.schedule}.${ScheduleReportsNames.baseTime}`}
               />
            </GridCentered>
            <GridCentered item xs={4}>
               <FormikTimePicker
                  name={`${ScheduleReportsNames.schedule}.${ScheduleReportsNames.baseTime}`}
               />
            </GridCentered>
         </Grid>

         <Grid container spacing={3}>
            <GridFormLabel item xs={3}>
               <FormLabel>{t('Repeats: ')}</FormLabel>
            </GridFormLabel>
            <GridCentered item>
               <FormikDropdown
                  name={`${ScheduleReportsNames.schedule}.${ScheduleReportsNames.recurrenceType}`}
                  options={RecurrenceType.options}
               />
            </GridCentered>
         </Grid>
         {recurrenceType !== RecurrenceType.enum.NEVER && (
            <Grid container spacing={3}>
               <GridFormLabel item xs={3}>
                  <FormLabel>{t('Repeats every ')}</FormLabel>
               </GridFormLabel>
               <GridCentered item>
                  <FormikNumberField
                     name={`${ScheduleReportsNames.schedule}.${ScheduleReportsNames.recurrenceUnits}`}
                     InputProps={{
                        endAdornment: (
                           <Typography variant={'body2'}>
                              {RecurrenceType[recurrenceType]?.noun}
                           </Typography>
                        ),
                     }}
                  />
               </GridCentered>
            </Grid>
         )}
         {(recurrenceType === RecurrenceType.enum.MONTHLY ||
            recurrenceType === RecurrenceType.enum.YEARLY) && (
            <Grid container spacing={3}>
               <GridFormLabel item xs={3}>
                  <FormLabel>{t('On the ')}</FormLabel>
               </GridFormLabel>
               <GridCentered item>
                  <FormikDropdown
                     name={`${ScheduleReportsNames.schedule}.${ScheduleReportsNames.dayOfMonth}`}
                     options={DayOfMonth.options}
                     // InputProps={{
                     //    endAdornment: <Typography variant={'body2'}>{t('of the month')}</Typography>,
                     // }}
                  />
               </GridCentered>
               <GridCentered item>
                  <FormLabel>{t('of the month')}</FormLabel>
               </GridCentered>
            </Grid>
         )}

         {timestamp && <LastUpdatedLabel date={timestamp} author={lastUser} />}

         <FormFooterButtons cancel={onClose} />

         {open && (
            <Dialog header={t('Params')} close={handleClose}>
               {audit && (
                  <AuditReportForm
                     values={auditParams}
                     onClose={handleClose}
                     onSubmit={setAuditReport}
                     formatChoosable={false}
                  />
               )}
               {user && (
                  <UserActivityReportForm
                     values={userParams}
                     onClose={handleClose}
                     onSubmit={setUserReport}
                  />
               )}
               {sftpActivity && (
                  <SFTPActivityReportForm
                     values={userParams}
                     onClose={handleClose}
                     onSubmit={setUserReport}
                  />
               )}
            </Dialog>
         )}
      </>
   );
});

export default AddEditScheduledReportForm;
